import api from '../../../lib/api'

// ── Response shapes ─────────────────────────────────────────────────────────

export interface LostSalesData {
  period_days: number
  by_reason: { reason: string; count: number }[]
  by_telecaller: { name: string; lost_count: number }[]
}

export interface RenewalSplitData {
  renewal: number
  new_business: number
  total: number
}

export interface RenewalBookHealthData {
  period_days: number
  retention_rate: number | null
  renewal_finalized_count: number
  renewal_issued_count: number
  premium_retained: number
  premium_due: number
  at_risk_count: number
  at_risk_premium: number
  overdue_count: number
  overdue_premium: number
  pipeline: {
    due_7: { count: number; premium: number }
    due_30: { count: number; premium: number }
    due_60: { count: number; premium: number }
  }
}

export interface RenewalBookTrendPoint {
  month: string
  renewal: number
  new_business: number
}

export interface OdDiscountData {
  '0': number
  '1-10': number
  '11-20': number
  '21-30': number
  '31-50': number
  '50+': number
}

export interface AvgDaysData {
  average_days: number
}

export interface CashPolicyEntry {
  lead_id: number
  customer_name: string
  policy_number: string
  premium_amount: number
  payment_reference: string | null
  policy_issued_at: string | null
}

export interface CashPolicyRegisterData {
  policies: CashPolicyEntry[]
  total_cash_collected: number
}

export interface RenewalForecastData {
  due_7: number
  due_30: number
  due_60: number
  overdue: number
  total_premium: number
}

export interface FraudLogEntry {
  id: number
  alert_type: string
  severity: string
  description: string | null
  status: string
  lead_id: number | null
  created_at: string | null
  resolved_at: string | null
}

export interface FraudSummaryData {
  total_alerts: number
  open_alerts: number
  critical_open: number
}

export interface NcCoverageData {
  total_lost: number
  covered: number
  rate: number
}

// ── Lost Leads Dashboard shapes ──────────────────────────────────────────────

export interface LostLeadsSummaryData {
  period_days: number
  total_lost: number
  by_status: { status: string; count: number }[]
  by_disposition: { disposition: string; count: number }[]
  by_telecaller: { name: string; lost_count: number }[]
  by_source: { source: string; count: number }[]
  trend: { date: string; count: number }[]
}

export interface CallRemarkEntry {
  call_no: number
  date: string | null
  disposition: string | null
  remarks: string
}

export interface LostLeadsListItem {
  lead_id: number
  customer_name: string
  vehicle_reg: string | null
  insurance_due_date: string | null
  status: string
  status_changed_at: string | null
  assigned_to_name: string
  lead_source: string | null
  total_calls: number
  first_contacted_at: string | null
  last_contacted_at: string | null
  days_to_lost: number | null
  disposition_sequence: string[]
  max_discount_offered: number | null
  all_remarks: CallRemarkEntry[]
  ai_journey_summary: string | null
}

export interface TelecallerLostPerformance {
  user_id: number
  name: string
  assigned_mtd: number
  policies_mtd: number
  lost_mtd: number
  conversion_rate: number
  avg_calls_per_lost: number | null
  avg_days_to_lost: number | null
  loss_reasons: { disposition: string; count: number }[]
}

export interface LostLeadsListParams {
  days?: number
  status?: string
  disposition?: string
  assigned_to?: number
  page?: number
  page_size?: number
}

export interface LostLeadsListResponse {
  total: number
  page: number
  page_size: number
  items: LostLeadsListItem[]
}

// ── My-performance shape (telecaller personal) ───────────────────────────────

export interface MyPerformanceData {
  calls_today: number
  calls_mtd: number
  policies_mtd: number
  lost_mtd: number
  open_leads: number
  conversion_rate: number
  pipeline: { status: string; count: number }[]
  recent_calls: {
    lead_id: number
    customer_name: string
    disposition: string | null
    duration_seconds: number | null
    called_at: string | null
  }[]
}

// ── Dashboard shapes ─────────────────────────────────────────────────────────

export interface DashboardSummaryData {
  total_leads: number
  new_leads_today: number
  calls_made_today: number
  policies_issued_today: number
  total_revenue_today: number
  active_telecallers: number
  open_fraud_alerts: number
}

export interface TelecallerPerformanceData {
  user_id: number
  name: string
  calls_today: number
  total_calls: number
  policies_issued: number
  lost_leads: number
  conversion_rate: number
}

export interface ConversionFunnelData {
  stage: string
  count: number
  percentage: number
}

export interface TrainingFlagWeightData {
  flag: string
  weighted_count: number
}

export interface TelecallerAIScoreData {
  user_id: number
  full_name: string
  weighted_overall: number | null
  weighted_sub_scores: Record<string, number>
  trend: 'improving' | 'declining' | 'stable' | 'insufficient_data'
  top_flags: TrainingFlagWeightData[]
  coaching_tip: string | null
  analyzed_call_count: number
  total_call_count: number
}

export interface CoachingPlanData {
  id: number
  coaching_plan: string
  weighted_overall: number | null
  analyzed_call_count: number
  created_at: string
}

// ── Service ─────────────────────────────────────────────────────────────────

export const reportingService = {
  async getDashboardSummary(): Promise<DashboardSummaryData> {
    const { data } = await api.get('/reports/dashboard')
    return data
  },

  async getTelecallerPerformance(): Promise<TelecallerPerformanceData[]> {
    const { data } = await api.get('/reports/telecaller-performance')
    return data
  },

  async getTelecallerAIPerformance(): Promise<TelecallerAIScoreData[]> {
    const { data } = await api.get('/reports/telecaller-ai-performance')
    return data
  },

  async generateTelecallerCoachingPlan(userId: number): Promise<CoachingPlanData> {
    const { data } = await api.post(`/reports/telecaller-ai-performance/${userId}/coaching-plan`)
    return data
  },

  async getTelecallerCoachingPlans(userId: number): Promise<CoachingPlanData[]> {
    const { data } = await api.get(`/reports/telecaller-ai-performance/${userId}/coaching-plans`)
    return data
  },

  async getConversionFunnel(): Promise<ConversionFunnelData[]> {
    const { data } = await api.get('/reports/conversion-funnel')
    return data
  },

  async getLostSales(days = 30): Promise<LostSalesData> {
    const { data } = await api.get('/reports/lost-sales', { params: { days } })
    return data
  },

  async getRenewalSplit(): Promise<RenewalSplitData> {
    const { data } = await api.get('/reports/renewal-split')
    return data
  },

  async getRenewalBookHealth(days = 30): Promise<RenewalBookHealthData> {
    const { data } = await api.get('/reports/renewal-book-health', { params: { days } })
    return data
  },

  async getRenewalBookTrend(months = 6): Promise<RenewalBookTrendPoint[]> {
    const { data } = await api.get('/reports/renewal-book-trend', { params: { months } })
    return data
  },

  async getOdDiscountUtilisation(): Promise<OdDiscountData> {
    const { data } = await api.get('/reports/od-discount-utilisation')
    return data
  },

  async getAvgDaysToPolicy(): Promise<AvgDaysData> {
    const { data } = await api.get('/reports/avg-days-to-policy')
    return data
  },

  async getCashPolicyRegister(): Promise<CashPolicyRegisterData> {
    const { data } = await api.get('/reports/cash-policy-register')
    return data
  },

  async getRenewalForecast(): Promise<RenewalForecastData> {
    const { data } = await api.get('/reports/renewal-forecast')
    return data
  },

  async getFraudLog(): Promise<FraudLogEntry[]> {
    const { data } = await api.get('/reports/fraud-log')
    return data
  },

  async getFraudSummary(): Promise<FraudSummaryData> {
    const { data } = await api.get('/reports/fraud-summary')
    return data
  },

  async getMyPerformance(): Promise<MyPerformanceData> {
    const { data } = await api.get('/reports/my-performance')
    return data
  },

  async getNcCoverage(): Promise<NcCoverageData> {
    const { data } = await api.get('/reports/negative-confirmation-coverage')
    return data
  },

  async getLostLeadsSummary(days = 30): Promise<LostLeadsSummaryData> {
    const { data } = await api.get('/reports/lost-leads-summary', { params: { days } })
    return data
  },

  async getLostLeadsList(params: LostLeadsListParams = {}): Promise<LostLeadsListResponse> {
    const { data } = await api.get('/reports/lost-leads-list', { params })
    return data
  },

  async generateLostLeadNarrative(leadId: number): Promise<{ narrative: string }> {
    const { data } = await api.post(`/reports/lost-leads/${leadId}/ai-narrative`)
    return data
  },

  async getTelecallerLostPerformance(leadId: number): Promise<TelecallerLostPerformance> {
    const { data } = await api.get('/reports/telecaller-lost-performance', { params: { lead_id: leadId } })
    return data
  },

  /** Triggers a browser CSV file download. */
  async downloadCallDetailExport(days = 7): Promise<void> {
    const response = await api.get('/reports/call-detail-export', {
      params: { days },
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(
      new Blob([response.data as BlobPart], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `call_detail_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },
}
